import { useState } from "react";
import { ago, api, Avatar, can, Drawer, Empty, fmtDate, Loading, Pill, setQuery, useLoad, useMe, useRoute } from "./lib";
import { DeptPage } from "./dept";
import { NewTask, TaskDrawer, TaskRow } from "./work";

export const OPP_KIND: Record<string, string> = { award: "Award", speaking: "Speaking", podcast: "Podcast", media: "Media", featuring: "Store featuring", grant: "Grant" };
export const OPP_STATUS: Record<string, { label: string; tone: string }> = {
  idea: { label: "Idea", tone: "" },
  researching: { label: "Researching", tone: "" },
  planned: { label: "Going for it", tone: "info" },
  applied: { label: "Applied", tone: "accent" },
  pitched: { label: "Pitched", tone: "accent" },
  accepted: { label: "Accepted", tone: "good" },
  won: { label: "Won", tone: "good" },
  declined: { label: "They said no", tone: "" },
  lost: { label: "Didn't win", tone: "" },
  passed: { label: "We passed", tone: "" },
  done: { label: "Done", tone: "good" },
};
const CLOSED = ["declined", "lost", "passed", "done"];

// Plain answer to "what's this waiting on?"
export function waitingOn(o: any, meId: string) {
  if (CLOSED.includes(o.status)) return { who: "Nobody", tone: "" };
  if (o.waiting_on_them) return { who: `Waiting on ${o.organizer || "them"}`, tone: "" };
  if (o.next_owner_id === meId) return { who: "Needs you", tone: "warn" };
  if (o.next_owner_name) return { who: `With ${o.next_owner_name}`, tone: "info" };
  return { who: "No owner yet", tone: "warn" };
}

export function PR() {
  const { me } = useMe();
  const route = useRoute();
  const { data, error, reload } = useLoad<any[]>("/api/opportunities", [route.q.get("opp")]);
  const [kind, setKind] = useState("");
  const [view, setView] = useState("active");
  const [adding, setAdding] = useState(false);
  const rows = (data || []).filter((o) => (!kind || o.kind === kind) && (view === "all" ? true : view === "closed" ? CLOSED.includes(o.status) : !CLOSED.includes(o.status)));
  const groups = [
    { title: "Needs you", items: rows.filter((o) => waitingOn(o, me.id).who === "Needs you") },
    { title: "With someone else", items: rows.filter((o) => waitingOn(o, me.id).tone === "info" || waitingOn(o, me.id).who === "No owner yet") },
    { title: "Waiting to hear back", items: rows.filter((o) => o.waiting_on_them && !CLOSED.includes(o.status)) },
    { title: "Closed", items: rows.filter((o) => CLOSED.includes(o.status)) },
  ].filter((g) => g.items.length);
  const toolbar = (
      <div className="row between">
        <div className="row">
          <div className="tabs">
            {[["active", "Active"], ["closed", "Closed"], ["all", "All"]].map(([k, l]) => <button key={k} className={view === k ? "on" : ""} onClick={() => setView(k!)}>{l}</button>)}
          </div>
          <select className="input" style={{ width: "auto" }} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Type">
            <option value="">All types</option>
            {Object.entries(OPP_KIND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="row">
          <a className="btn" href="#/calendar?types=opportunity">See on the calendar</a>
          {can(me, "pr.edit") && <button className="btn primary" onClick={() => setAdding(true)}>Add opportunity</button>}
        </div>
      </div>
  );
  return (
    <DeptPage area="pr" take={data && <ClaudeTake opps={data} onOpen={(id) => setQuery({ opp: id })} />} toolbar={toolbar}>
      {!data ? <Loading error={error} /> : !groups.length ? <Empty>Nothing here.</Empty> : groups.map((g) => (
        <section key={g.title} className="card" style={{ padding: "8px 16px" }}>
          <h2 style={{ margin: "10px 0 4px" }}>{g.title} <span className="muted small">{g.items.length}</span></h2>
          <div className="list">
            {g.items.map((o) => (
              <div key={o.id} className="item clickable" onClick={() => setQuery({ opp: o.id })} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setQuery({ opp: o.id })}>
                <div className="grow">
                  <div className="row" style={{ gap: 8 }}><span className="title">{o.name}</span><Pill>{OPP_KIND[o.kind]}</Pill></div>
                  <div className="small muted" style={{ marginTop: 2 }}>{o.next_action}</div>
                </div>
                <StatusPill o={o} />
                {o.deadline_on && !["applied", "pitched", "accepted", "won", "done"].includes(o.status) && <Pill tone={o.deadline_on <= new Date().toISOString().slice(0, 10) ? "bad" : ""}>Due {fmtDate(o.deadline_on)}</Pill>}
                {o.event_on && <span className="small muted hide-sm">{fmtDate(o.event_on)}</span>}
              </div>
            ))}
          </div>
        </section>
      ))}
      <p className="muted tiny" style={{ margin: 0 }}>Loaded from your Awards, Recognition & Speaking Strategy doc (Oct 29). Statuses are as of that date until someone updates them.</p>
      {route.q.get("opp") && <OppDrawer id={route.q.get("opp")!} onClose={() => setQuery({ opp: null })} onChange={reload} />}
      {route.q.get("task") && <TaskDrawer id={route.q.get("task")!} onClose={() => setQuery({ task: null })} onChange={reload} />}
      {adding && <OppForm onClose={() => setAdding(false)} onSaved={(id) => (setAdding(false), reload(), setQuery({ opp: id }))} />}
    </DeptPage>
  );
}

function StatusPill({ o }: { o: any }) {
  const s = OPP_STATUS[o.status] ?? { label: o.status, tone: "" };
  return <Pill tone={s.tone}>{s.label}</Pill>;
}

function OppForm({ opp, onClose, onSaved }: { opp?: any; onClose: () => void; onSaved: (id: string) => void }) {
  const { me, toast } = useMe();
  const users = useLoad<any[]>("/api/users").data || [];
  const o = opp || { kind: "award", status: "idea", priority: "normal", next_owner_id: me.id };
  return (
    <Drawer title={opp ? `Edit ${opp.name}` : "Add opportunity"} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          const fd = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, any>;
          fd.waiting_on_them = fd.waiting_on_them === "on";
          try {
            const r = opp ? await api(`/api/opportunities/${opp.id}`, { method: "PATCH", body: fd }) : await api("/api/opportunities", { method: "POST", body: fd });
            toast("Saved");
            onSaved(opp?.id ?? r.id);
          } catch (err: any) {
            toast(err.message, true);
          }
        }}
      >
        <label className="field">Name<input className="input" name="name" required defaultValue={o.name} /></label>
        <div className="form-2">
          <label className="field">Type<select className="input" name="kind" defaultValue={o.kind}>{Object.entries(OPP_KIND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <label className="field">Status<select className="input" name="status" defaultValue={o.status}>{Object.entries(OPP_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></label>
          <label className="field">Organizer<input className="input" name="organizer" defaultValue={o.organizer} /></label>
          <label className="field">Where<input className="input" name="location" defaultValue={o.location} /></label>
          <label className="field">Apply or submit by<input className="input" type="date" name="deadline_on" defaultValue={o.deadline_on ?? ""} /></label>
          <label className="field">Event date<input className="input" type="date" name="event_on" defaultValue={o.event_on ?? ""} /></label>
          <label className="field">Event ends<input className="input" type="date" name="event_end_on" defaultValue={o.event_end_on ?? ""} /></label>
          <label className="field">Follow up on<input className="input" type="date" name="follow_up_on" defaultValue={o.follow_up_on ?? ""} /></label>
          <label className="field">Cost<input className="input" name="fee" defaultValue={o.fee ?? ""} placeholder="e.g. $225" /></label>
          <label className="field">Priority<select className="input" name="priority" defaultValue={o.priority}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
        </div>
        <label className="field">Next step<input className="input" name="next_action" defaultValue={o.next_action} /></label>
        <div className="form-2">
          <label className="field">Whose next step<select className="input" name="next_owner_id" defaultValue={o.next_owner_id ?? ""}><option value="">Nobody yet</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
          <label className="check" style={{ alignSelf: "end" }}><input type="checkbox" name="waiting_on_them" defaultChecked={!!o.waiting_on_them} /> Waiting to hear back from them</label>
        </div>
        <label className="field">Link<input className="input" type="url" name="url" defaultValue={o.url ?? ""} placeholder="https://" /></label>
        <label className="field">Contact<input className="input" name="contact" defaultValue={o.contact} /></label>
        <label className="field">Why it fits<textarea className="input" name="why" defaultValue={o.why} /></label>
        <label className="field">Requirements<textarea className="input" name="requirements" defaultValue={o.requirements} /></label>
        <label className="field">Talk<input className="input" name="talk" defaultValue={o.talk ?? ""} /></label>
        <label className="field">Notes<textarea className="input" name="notes" defaultValue={o.notes} /></label>
        <div className="row"><button className="btn primary">Save</button></div>
      </form>
    </Drawer>
  );
}

function OppDrawer({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => void }) {
  const { me, toast } = useMe();
  const { data: o, error, reload } = useLoad<any>(`/api/opportunities/${id}`, [id]);
  const [edit, setEdit] = useState(false);
  const [newTask, setNewTask] = useState(false);
  const [comment, setComment] = useState("");
  if (!o) return <Drawer title="Opportunity" onClose={onClose}><Loading error={error} /></Drawer>;
  const w = waitingOn(o, me.id);
  const setStatus = async (status: string) => {
    try {
      await api(`/api/opportunities/${id}`, { method: "PATCH", body: { status } });
      reload();
      onChange();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const quick: [string, string][] = ({
    idea: [["planned", "Go for it"], ["passed", "Pass"]],
    researching: [["planned", "Go for it"], ["passed", "Pass"]],
    planned: [["applied", "Mark applied"], ["pitched", "Mark pitched"], ["passed", "Pass"]],
    applied: [["accepted", "Accepted"], ["won", "Won"], ["declined", "They said no"], ["lost", "Didn't win"]],
    pitched: [["accepted", "They said yes"], ["declined", "They said no"]],
    accepted: [["done", "Done"]],
  } as Record<string, [string, string][]>)[o.status] ?? [];
  return (
    <Drawer title={o.name} onClose={onClose} sub={<div className="row"><Pill>{OPP_KIND[o.kind]}</Pill><StatusPill o={o} /><Pill tone={w.tone}>{w.who}</Pill></div>}>
      <section className="card stack">
        <div className="row between"><h2>Where it stands</h2>{o.can_edit && <button className="btn sm" onClick={() => setEdit(true)}>Edit</button>}</div>
        <div className="banner info small"><div><strong>Next step{o.next_owner_name ? ` (${o.next_owner_name})` : ""}:</strong> {o.next_action || "Not set"}</div></div>
        {o.can_edit && quick.length > 0 && <div className="row">{quick.map(([s, l]) => <button key={s} className={`btn sm ${s === "planned" || s === "applied" ? "primary" : ""}`} onClick={() => setStatus(s)}>{l}</button>)}</div>}
        <div className="form-2 small">
          <div><div className="muted tiny">Apply by</div>{o.deadline_on ? fmtDate(o.deadline_on) : "No deadline"}{o.deadline_note && <div className="muted tiny">{o.deadline_note}</div>}</div>
          <div><div className="muted tiny">Event</div>{o.event_on ? `${fmtDate(o.event_on)}${o.event_end_on ? ` to ${fmtDate(o.event_end_on)}` : ""}` : "None"}{o.location && <div className="muted tiny">{o.location}</div>}</div>
          <div><div className="muted tiny">Applied or pitched</div>{o.applied_on ? fmtDate(o.applied_on) : "Not yet"}</div>
          <div><div className="muted tiny">Follow up</div>{o.follow_up_on ? fmtDate(o.follow_up_on) : "Not set"}</div>
          <div><div className="muted tiny">Cost</div>{o.fee || "Unknown"}</div>
          <div><div className="muted tiny">Organizer</div>{o.organizer || "Unknown"}</div>
        </div>
        {o.contact && <div className="small"><span className="muted">Contact: </span>{o.contact}</div>}
        {o.url && <a className="small" href={o.url} target="_blank" rel="noreferrer">Open their page</a>}
      </section>
      {(o.why || o.requirements || o.talk || o.notes) && (
        <section className="card stack small">
          {o.why && <div><strong>Why it fits: </strong>{o.why}</div>}
          {o.talk && <div><strong>Talk: </strong>{o.talk}</div>}
          {o.requirements && <div><strong>Requirements: </strong>{o.requirements}</div>}
          {o.notes && <div style={{ whiteSpace: "pre-wrap" }}><strong>Notes: </strong>{o.notes}</div>}
          <div className="muted tiny">Source: {o.source}</div>
        </section>
      )}
      <section className="card stack">
        <div className="row between"><h2>Tasks</h2><button className="btn sm" onClick={() => setNewTask(true)}>Add task</button></div>
        {o.tasks.length ? <div className="list">{o.tasks.map((t: any) => <TaskRow key={t.id} t={t} onOpen={(tid) => setQuery({ opp: null, task: tid })} />)}</div> : <span className="muted small">None.</span>}
      </section>
      <section className="card stack">
        <h2>Comments</h2>
        {o.comments.map((c: any) => (
          <div key={c.id} className="comment"><div className="who row" style={{ gap: 8 }}><Avatar id={c.author_id} name={c.author_name} hasPhoto={c.author_has_photo} size={22} />{c.author_name} <span className="muted tiny">{ago(c.created_at)}</span></div><p>{c.body}</p></div>
        ))}
        <form className="row" onSubmit={async (e) => { e.preventDefault(); if (!comment.trim()) return; await api("/api/comments", { method: "POST", body: { record_type: "opportunity", record_id: id, body: comment } }).catch((err) => toast(err.message, true)); setComment(""); reload(); }}>
          <input className="input" style={{ flex: 1 }} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment" />
          <button className="btn">Post</button>
        </form>
      </section>
      <section className="card stack">
        <h2>History</h2>
        <div className="timeline">{o.activity.map((a: any) => <div key={a.id}><time>{ago(a.created_at)}</time><span>{a.actor_name || "System"}: {a.action.replace(/^opportunity\./, "").replace(/_/g, " ")}</span></div>)}</div>
        {!o.activity.length && <span className="muted small">Imported from the strategy doc.</span>}
      </section>
      {edit && <OppForm opp={o} onClose={() => setEdit(false)} onSaved={() => (setEdit(false), reload(), onChange())} />}
      {newTask && <NewTask link={{ type: "opportunity", id, label: o.name }} onClose={() => setNewTask(false)} onCreated={() => (setNewTask(false), reload())} />}
    </Drawer>
  );
}

// Claude's read on the whole list: what needs her this week, what to go after, what to let go.
function ClaudeTake({ opps, onOpen }: { opps: any[]; onOpen: (id: string) => void }) {
  const { toast } = useMe();
  const { data, reload } = useLoad<any>("/api/insights/pr");
  const [busy, setBusy] = useState(false);
  const [showLetGo, setShowLetGo] = useState(false);
  if (!data) return null;
  const byId = new Map(opps.map((o) => [o.id, o]));
  const run = async () => {
    setBusy(true);
    try {
      await api("/api/insights/pr", { method: "POST" });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const ins = data.insight?.result;
  const live = (list: any[]) => (list ?? []).filter((x) => byId.has(x.id) && !CLOSED.includes(byId.get(x.id).status));
  const row = (x: any, extra?: string) => (
    <div key={x.id} className="clickable" style={{ padding: "8px 6px", borderRadius: 8 }} onClick={() => onOpen(x.id)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(x.id)}>
      <div style={{ fontWeight: 600, fontSize: 14 }}>{byId.get(x.id)?.name}</div>
      <div className="small muted">{x.why}</div>
      {extra && <div className="small" style={{ marginTop: 2 }}><span style={{ color: "var(--accent-ink)", fontWeight: 600 }}>Next: </span>{extra}</div>}
    </div>
  );
  return (
    <section className="card stack ai-card">
      <div className="row between">
        <div>
          <span className="ai-spark" /><strong className="ai-mark">Claude's take</strong>
          {data.insight && <span className="muted tiny"> · {ago(data.insight.created_at)} · AI suggestion, check it</span>}
        </div>
        {data.can_refresh && data.connected && (
          <button className="btn sm" disabled={busy} onClick={run}>{busy ? "Thinking…" : data.insight ? "Refresh" : "Ask Claude what to go after"}</button>
        )}
      </div>
      {!data.connected ? (
        <span className="muted small">AI isn't connected yet.</span>
      ) : !ins ? (
        <span className="muted small">Claude reads every open opportunity with today's date, deadlines, costs and where Momeaze is, then tells you what needs you this week, what to go after, and what to let go. About 5 cents a run.</span>
      ) : (
        <>
          <div style={{ fontSize: 15 }}>{ins.headline}</div>
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))" }}>
            <div>
              <div className="tiny" style={{ fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--accent-ink)", marginBottom: 4 }}>Needs you this week</div>
              {live(ins.do_now).length ? live(ins.do_now).map((x) => row(x, x.next_step)) : <span className="muted small">Nothing urgent.</span>}
            </div>
            <div>
              <div className="tiny" style={{ fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 4 }}>Worth going after</div>
              {live(ins.go_after).length ? live(ins.go_after).map((x) => row(x)) : <span className="muted small">None right now.</span>}
            </div>
          </div>
          {ins.watch_out && <div className="banner warn small"><div><strong>Watch out: </strong>{ins.watch_out}</div></div>}
          {live(ins.let_go).length > 0 && (
            <div>
              <button className="btn ghost sm" style={{ padding: 0 }} onClick={() => setShowLetGo(!showLetGo)}>{showLetGo ? "Hide" : `Let go for now (${live(ins.let_go).length})`}</button>
              {showLetGo && <div style={{ marginTop: 6 }}>{live(ins.let_go).map((x) => row(x))}</div>}
            </div>
          )}
        </>
      )}
    </section>
  );
}
